import { FC, useContext, useMemo, useRef, useState } from 'react';
import { Form, IFormOperations } from '@abbott-components/core';
import { JSONSchema7, JSONSchema7Type } from 'json-schema';
import { put } from '../Shared/ajax';
import { useHover, useOnClickOutside } from 'usehooks-ts';
import { useSnackbar } from 'notistack';
import ControlBuildTools from './ControlBuildTools';
import JSONSchema7Properties from '../Shared/jSONSchema7Properties';
import Stack from '@mui/material/Stack';
import {
  LastUpdatedInfo,
  WorkflowEngineActionKind,
  WorkflowEngineContext,
  WorkflowEngineMode
} from '../WorkflowEngine/workflowEngineReducer';
import isCustomIdUnique from '../Shared/jsonSchemaUtilities/isCustomIdUnique';
import isBuilding from '../Shared/jsonSchemaUtilities/isBuilding';
import ControlState from '../Shared/controlState';
import { Theme } from '@mui/system/createTheme/createTheme';
import Popover from '@mui/material/Popover';
import PopoverContent, { PopoverKind } from './PopoverContent';
import controlConfig from '../Controls/controlDictionary';
import { useElementSize } from 'usehooks-ts';
import { AppContext } from '../App/appReducer';
import { AppActionKind, AppPageKind } from '../App/types';
import { Grid } from '@mui/material';

interface UpdatePageRequest {
  externalId: string;
  metadata: string;
}

interface UpdatePageResponse {
  externalId: string;
  lastUpdatedDate: string;
  lastUpdatedBy: string;
  metadata: string;
}

const ControlEngine: FC<{
  control: ControlState;
  readOnly?: boolean;
}> = ({ control, readOnly }) => {
  const controlNotAvailableMsg =
    'This component type is not available in this version of the application.';

  const { dispatch: appDispatch } = useContext(AppContext);

  const { state: workflowEngineState, dispatch } = useContext(
    WorkflowEngineContext
  );

  const { pageId, schema, building, kind } = control;

  const controlTools = controlConfig[kind];

  const hoverRef = useRef<HTMLDivElement>(null);

  const [wrapperRef, wrapperSize] = useElementSize();

  const isHover = useHover(hoverRef);

  const { enqueueSnackbar } = useSnackbar();

  const [popoverAnchorElement, setPopoverAnchorElement] =
    useState<HTMLButtonElement | null>(null);
  const [popoverKind, setPopoverKind] = useState<PopoverKind | null>(null);

  const handleOpen = (
    target: HTMLButtonElement | null,
    popoverKind: PopoverKind
  ) => {
    setPopoverKind(popoverKind);
    setPopoverAnchorElement(target);
  };

  const handleClose = () => {
    setPopoverAnchorElement(null);
  };

  const isPopoverOpen = Boolean(popoverAnchorElement);

  const formRef = useRef<IFormOperations>();

  const handleControlWrapperClick = (e: React.MouseEvent<HTMLElement>) => {
    const htmlElement = e.currentTarget as HTMLElement;

    const isButtonClick =
      htmlElement &&
      htmlElement.closest &&
      !!htmlElement.closest('.MuiIconButton-root');

    if (
      readOnly ||
      isButtonClick ||
      isPopoverOpen ||
      isBuilding(workflowEngineState.pages)
    ) {
      return; // exit
    }

    e.preventDefault();
    e.stopPropagation();

    dispatch({
      type: WorkflowEngineActionKind.CONTROL_OPEN_BUILDING,
      payload: control
    });
  };

  const handleControlWrapperClickOutside = async (e: MouseEvent) => {
    if (!building) return;

    const htmlElement = e.target as HTMLElement;

    // This check is important because dropdowns are created in the DOM outside the clickable area
    const isDropDownClosing =
      htmlElement &&
      htmlElement.closest &&
      !!htmlElement.closest('.MuiMenuItem-root');

    // This check is important because autocomplete options are created in the DOM outside the clickable area
    const isAutoCompleteOption =
      htmlElement && htmlElement.classList?.contains('MuiAutocomplete-option');

    const isSelectDropDown =
      htmlElement && htmlElement.classList?.contains('MuiBackdrop-root');

    const isRTEIcon =
      htmlElement && htmlElement.classList?.contains('MuiSvgIcon-root');
    const isRTETextbox =
      htmlElement && htmlElement.classList?.contains('MuiInputBase-input');

    const isCalendar =
      htmlElement &&
      htmlElement.closest &&
      !!htmlElement?.closest('.MuiCalendarPicker-root');

    const isBackdrop =
      htmlElement &&
      htmlElement.closest &&
      !!htmlElement?.closest('.MuiBackdrop-root');

    const isDialog =
      htmlElement &&
      htmlElement.closest &&
      !!htmlElement?.closest('.MuiDialog-container');

    if (
      isDropDownClosing ||
      isPopoverOpen ||
      isAutoCompleteOption ||
      isSelectDropDown ||
      isRTEIcon ||
      isRTETextbox ||
      isCalendar ||
      isBackdrop ||
      isDialog
    )
      return;

    formRef.current?.submitForm();
  };

  const savePageSchema = async (pageId: string, pageSchema: JSONSchema7) => {
    try {
      const response = await put<UpdatePageResponse, UpdatePageRequest>(
        'DynamicWorkflowPage',
        {
          externalId: pageId,
          metadata: JSON.stringify(pageSchema)
        }
      );

      enqueueSnackbar('Configuration successfully saved!', {
        variant: 'success'
      });

      dispatch({
        type: WorkflowEngineActionKind.SET_LAST_UPDATED_INFO,
        payload: {
          externalId: response.externalId,
          lastUpdatedDate: response.lastUpdatedDate,
          lastUpdatedBy: response.lastUpdatedBy,
          metadata: response.metadata
        } as LastUpdatedInfo
      });
    } catch (err) {
      const errorMsg = (err as Error).message;
      if (errorMsg.startsWith('This workflow has been locked')) {
        appDispatch({
          type: AppActionKind.SET_APPPAGE,
          payload: AppPageKind.LANDING
        });
      }
      enqueueSnackbar(errorMsg, { variant: 'error' });
    }
  };

  const handleFormSubmit = async (data: Record<string, unknown>) => {
    if (workflowEngineState.isReadOnlyView) {
      dispatch({
        type: WorkflowEngineActionKind.CONTROL_CLOSE_BUILDING,
        payload: control
      });
      return;
    }

    const controlProperties = schema?.properties as JSONSchema7Properties;

    // update control schema values from from form data
    Object.getOwnPropertyNames(controlProperties).forEach(
      (propName: string) => {
        if (propName === 'conditional') return;

        const value = data[propName] as JSONSchema7Type;
        controlProperties[propName].default = value as JSONSchema7Type;
      }
    );

    const customId = (controlProperties['id'] as JSONSchema7).default as string;

    const isUnique = !isCustomIdUnique(
      schema?.$id as string,
      workflowEngineState.pages,
      customId
    );

    if (isUnique) {
      enqueueSnackbar(
        `The id ${customId} is already used by another control.`,
        {
          variant: 'error'
        }
      );
      return;
    }

    dispatch({
      type: WorkflowEngineActionKind.CONTROL_UPDATE,
      payload: {
        ...control,
        schema: {
          ...control.schema,
          properties: controlProperties
        },
        building: false
      }
    });

    const currentPage = workflowEngineState.pages.find(
      (page) => page.id == pageId
    );
    await savePageSchema(pageId as string, currentPage?.schema as JSONSchema7);
  };

  const renderBulding = () => {
    if (!controlTools) return <>{controlNotAvailableMsg}</>;

    let controlToBuild = <b>Not Implemented</b>;

    const ControlBuilding = controlTools.Engine;

    controlToBuild = <ControlBuilding {...control} />;

    const defaultValues: JSONSchema7Type = {};

    const controlProperties = schema?.properties as JSONSchema7Properties;

    // generate default values from control JSON schema
    Object.getOwnPropertyNames(controlProperties).forEach(
      (propName: string) => {
        const value = controlProperties[propName].default as JSONSchema7Type;
        defaultValues[propName] = value;
      }
    );

    const validationSchema = controlTools.yupSchemaEngine(controlProperties);

    return (
      <Form
        formRef={formRef}
        onFormSubmit={handleFormSubmit}
        validationSchema={validationSchema}
        formOptions={{
          mode: 'onChange',
          defaultValues
        }}
      >
        {controlToBuild}
      </Form>
    );
  };

  const renderEntry = () => {
    if (!controlTools) return <>{controlNotAvailableMsg}</>;
    const ControlEntry = controlTools.Entry;
    return (
      <ControlEntry
        {...control}
        wrapper={wrapperRef}
        wrapperSize={wrapperSize}
      />
    );
  };

  const controlWrapperStyles = (theme: Theme) => ({
    backgroundColor:
      !readOnly && (isHover || building || isPopoverOpen)
        ? theme.palette.grey[100]
        : theme.palette.common.white,
    cursor: !readOnly ? 'pointer' : 'inherit',
    '& .MuiOutlinedInput-input': {
      cursor: !readOnly && !building ? 'pointer' : 'inherit',
      backgroundColor: theme.palette.common.white
    },
    padding: readOnly ? 'initial' : '36px 15px 15px 15px',
    margin: '5px 0'
  });

  const isControlConditionalEnabled = (ctrl: ControlState) => {
    if (workflowEngineState.mode !== WorkflowEngineMode.BUILD) return false;

    const properties = ctrl.schema?.properties as JSONSchema7Properties;
    return (
      Object.keys(properties).includes('conditional') &&
      (properties['conditional'].default as boolean)
    );
  };

  useOnClickOutside(hoverRef, handleControlWrapperClickOutside);

  const memoDependencies = [building, wrapperSize];

  return (
    <Stack
      id={schema?.$id}
      ref={hoverRef}
      onClick={handleControlWrapperClick}
      sx={(theme) => ({
        ...controlWrapperStyles(theme),
        opacity: isControlConditionalEnabled(control) ? 0.7 : 1
      })}
    >
      <Stack
        ref={wrapperRef}
        data-part="ControlWrapper"
        sx={{ position: 'relative' }}
        direction="column"
      >
        <Stack sx={{ flexGrow: 1 }}>
          <Grid container>
            {building
              ? useMemo(() => renderBulding(), memoDependencies)
              : useMemo(() => renderEntry(), memoDependencies)}
          </Grid>
        </Stack>
        <Stack sx={{ position: 'absolute', top: '-32px', right: '-10px' }}>
          {!readOnly && !workflowEngineState.isReadOnlyView && (
            <ControlBuildTools
              isActive={isHover || isPopoverOpen}
              control={control}
              openPopover={handleOpen}
            />
          )}
        </Stack>
        <Popover
          open={isPopoverOpen}
          anchorEl={popoverAnchorElement}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left'
          }}
        >
          <PopoverContent
            popoverKind={popoverKind}
            control={control}
            onClose={handleClose}
          />
        </Popover>
      </Stack>
    </Stack>
  );
};

export default ControlEngine;
